import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatSortModule, Sort, SortDirection } from '@angular/material/sort';
import moment from 'moment-timezone';

import { LegfiCurrencyPipe } from '../../../../../pipes/format/legfi-currency.pipe';
import {
    TabularReportColumn,
    TabularReportRow,
} from '../../../../app-layout/shared/tabular-report/tabular-report.config';
import { ReferenceColumnComponent } from '../../../../app-layout/shared/tabular-report/reference-column/reference-column.component';
import { NestedSortComparatorService } from '../../../../../services/nested-sort-comparator.service';
import { TableTypeNames } from '../../../../../services/column.service';
import { StoredSortState } from '../../../../../services/filter-sort-persistence.service';
import { TablePreferencesStore, TABLE_PREFERENCES_STORE } from '../../../../../services/table-preferences/table-preferences-store';

/**
 * V2 Layout hierarchical report table.
 *
 * Renders nested/accordion-style financial reports (Balance Sheet, P&L, etc.)
 * with expand/collapse, indentation levels, and currency formatting.
 *
 * Lives in v2-layout/reports/shared/ — reusable across all hierarchical reports.
 *
 * Sorting is OPT-IN per column (`TabularReportColumn.sortable`): a report enables it by
 * marking columns sortable and passing a `tableType`. Reports that don't opt in render exactly
 * as before. Sorting reorders siblings within each group (report-level totals stay pinned) while
 * preserving row-object identity, so collapse/indent keep working; comparison is delegated to the
 * shared {@link NestedSortComparatorService}, and the active sort persists via the table
 * preferences store (localStorage today, backend later).
 */
@Component({
    selector: 'app-report-table',
    templateUrl: './report-table.component.html',
    styleUrls: ['./report-table.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        MatIconModule,
        MatSortModule,
        LegfiCurrencyPipe,
        ReferenceColumnComponent,
    ],
})
export class ReportTableComponent implements OnChanges {
    /** Raw hierarchical rows from the API (with children arrays) */
    @Input() rows: TabularReportRow[] = [];

    /** Column definitions from the API */
    @Input() columns: TabularReportColumn[] = [];

    /** Organization name (displayed centered above the table) */
    @Input() orgName = '';

    /** Report title (displayed in uppercase below org name) */
    @Input() reportTitle = '';

    /** Date subtitle (e.g. "As of 03/27/2026" or date range) */
    @Input() dateSubtitle = '';

    /** The column valueKey that should be clickable for navigation (e.g. 'label') */
    @Input() clickableCategoryColumn = '';

    /** When true, constrain max-width on wide screens (good for 2-col reports like budget summary) */
    @Input() compact = false;

    /**
     * When true, rows that are plain data leaves (no children, not category/subtotal/total)
     * ignore the hierarchy indent so their first column aligns with the table header.
     * Use for transaction-style reports (P&L Detail) where leaves carry actual column values.
     */
    @Input() unindentLeaves = false;

    /**
     * When true, data-leaf rows render their label column completely flush to the header
     * baseline — no hierarchy indent and no negative-margin compensation at all.
     *
     * Unlike {@link unindentLeaves} (which keeps leaves indented under their parent category,
     * clearing the collapse chevron), this fully left-aligns the first column. Use when the
     * label column carries a short data value (e.g. Account Register's Entry Date) that is
     * immediately followed by a wide free-text column (Description): the indent + negative
     * margin-right would otherwise pull the neighbouring column left, on top of the indented
     * label text, making the columns overlap when the window is compressed (SD-6870). The
     * hierarchy stays legible via the category-header rows, which keep their indent.
     */
    @Input() flushLeaves = false;

    /**
     * Table identity for persisting the active sort (per-user, via the preferences store).
     * Omit to keep sort session-only (no persistence).
     */
    @Input() tableType?: TableTypeNames;

    /** Initial sort to apply when nothing is persisted yet. */
    @Input() defaultSort?: StoredSortState;

    /** Emits when a clickable row is clicked (e.g. account drill-through) */
    @Output() categoryClick = new EventEmitter<TabularReportRow>();

    /** Flattened rows for display */
    displayRows: TabularReportRow[] = [];

    /** Visible columns (display !== false) */
    visibleColumns: TabularReportColumn[] = [];

    /** The first column (label) — used for collapse icon */
    labelColumn: TabularReportColumn | null = null;

    /** Value columns (everything after label) */
    valueColumns: TabularReportColumn[] = [];

    /** Active sort column valueKey, or null when unsorted */
    sortColumn: string | null = null;

    /** Active sort direction ('' when unsorted) */
    sortDirection: SortDirection = '';

    private readonly _cdr = inject(ChangeDetectorRef);
    private readonly _comparator = inject(NestedSortComparatorService);
    private readonly _injector = inject(Injector);

    private _sortInitialized = false;
    private _prefs?: TablePreferencesStore;

    /**
     * Resolve the preferences store lazily — only reports that opt into sorting (a `tableType`)
     * ever touch it. This keeps the shared component (and the 26 reports that don't sort) free of
     * the store's transitive dependencies, so their DI graph and specs are unchanged.
     */
    private prefs(): TablePreferencesStore {
        return this._prefs ??= this._injector.get(TABLE_PREFERENCES_STORE);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['columns']) {
            this.visibleColumns = this.columns.filter(c => c.display !== false);
            this.labelColumn = this.visibleColumns.find(c => c.collapse) || this.visibleColumns[0] || null;
            this.valueColumns = this.visibleColumns.filter(c => c !== this.labelColumn);
            this.initializeSort();

            // Drop a stale sort if the regenerated columns no longer offer it as sortable.
            if (this.sortColumn && !this.visibleColumns.some(c => c.valueKey === this.sortColumn && c.sortable)) {
                this.sortColumn = null;
                this.sortDirection = '';
            }
        }
        if (changes['rows'] || changes['columns']) {
            this.buildDisplayRows();
        }
    }

    /** Toggle expand/collapse on a collapsible row */
    toggleRow(row: TabularReportRow): void {
        if (!row.collapsible) { return; }
        row.show = !row.show;
        this.buildDisplayRows();
        this._cdr.markForCheck();
    }

    /** Whether a column opts into sorting. Columns are sortable only when they explicitly
     * set `sortable: true` — every other report leaves it unset, so their behavior is unchanged. */
    isSortable(column: TabularReportColumn | null): boolean {
        return !!column?.sortable;
    }

    /** Numeric columns put the sort arrow before the (right-aligned) label, matching the
     * app's existing sortable-table convention; label/text columns keep the arrow after. */
    sortArrowPosition(column: TabularReportColumn): 'before' | 'after' {
        return this.isNumericFormat(column) ? 'before' : 'after';
    }

    /** React to a header click from MatSort — re-sort the (already hierarchical) rows and persist. */
    onSortChange(sort: Sort): void {
        this.sortColumn = sort.direction ? sort.active : null;
        this.sortDirection = sort.direction;
        this.persistSort();
        this.buildDisplayRows();
        this._cdr.markForCheck();
    }

    /**
     * Check if a row's label is clickable for drill-through navigation.
     * Matches the old tabular-report logic exactly.
     */
    isRowClickable(row: TabularReportRow): boolean {
        if (!this.clickableCategoryColumn) { return false; }
        if (row.subTotal || row.grandTotal) { return false; }
        return !!(row.categoryId || row.accountId);
    }

    /** Handle click on a clickable label — emit the row for parent to navigate */
    onCategoryClick(event: Event, row: TabularReportRow): void {
        if (this.isRowClickable(row)) {
            event.stopPropagation(); // Prevent accordion toggle
            this.categoryClick.emit(row);
        }
    }

    /** Format a cell value based on column format */
    formatValue(row: TabularReportRow, column: TabularReportColumn): string | null {
        const val = row[column.valueKey];
        if (val == null) { return null; }
        if (column.hideZero && val === 0) { return null; }
        return val;
    }

    /** Whether a value should be formatted as currency */
    isMoney(column: TabularReportColumn): boolean {
        return column.format === 'money';
    }

    /** Whether a value should be rendered as a reference (type+id → dialog link) */
    isReference(column: TabularReportColumn): boolean {
        return column.format === 'reference';
    }

    /** Columns whose format is not money/percent — the "text" columns that total rows collapse. */
    private isTextColumn(column: TabularReportColumn): boolean {
        return !this.isMoney(column) && !this.isPercent(column);
    }

    /** A row is a subtotal/grand-total row if it's flagged as such. These rows only need to
     * display the label + money columns; text columns are blank and just create empty space. */
    private isTotalRow(row: TabularReportRow): boolean {
        return !!(row.subTotal || row.grandTotal);
    }

    /** For total rows, return only the money-format value columns (description column is
     * implicit in the widened label cell). For normal rows, return every value column. */
    valueColumnsForRow(row: TabularReportRow): TabularReportColumn[] {
        if (this.isTotalRow(row)) {
            return this.valueColumns.filter(c => !this.isTextColumn(c));
        }
        return this.valueColumns;
    }

    /** Flex-grow for the label cell so a total row's label spans through every text
     * value column that we skipped. Normal rows stay at 1 (matches existing layout). */
    labelFlexGrow(row: TabularReportRow): number {
        if (!this.isTotalRow(row)) { return 1; }
        const skippedTextCols = this.valueColumns.filter(c => this.isTextColumn(c)).length;
        return 1 + skippedTextCols;
    }

    /** Whether a value should be formatted as a percentage */
    isPercent(column: TabularReportColumn): boolean {
        return column.format === 'percent';
    }

    /** Whether a column has a numeric format (money, percent, number) — used for right-alignment */
    isNumericFormat(column: TabularReportColumn): boolean {
        return column.format === 'money' || column.format === 'percent' || column.format === 'number';
    }

    trackByIndex(index: number): number {
        return index;
    }

    /** Padding in px for the label cell */
    labelPadding(row: TabularReportRow): string {
        const level = row.level || 0;
        if (this.flushLeaves && this.isDataLeaf(row)) {
            return '8px';
        }
        if (this.unindentLeaves && this.isDataLeaf(row) && level > 0) {
            return `${8 + (level - 1) * 20 + 24}px`;
        }
        return `${8 + level * 20}px`;
    }

    /** Negative margin-right to compensate for indent so value columns stay aligned */
    labelMarginRight(row: TabularReportRow): string {
        const level = row.level || 0;
        if (level === 0) { return '0'; }
        if (this.flushLeaves && this.isDataLeaf(row)) {
            // Flush data leaves take no indent, so there is nothing to compensate for.
            return '0';
        }
        if (this.unindentLeaves && this.isDataLeaf(row)) {
            // Data leaves in unindentLeaves mode: compensate for the chevron-clearing indent
            return `-${(level - 1) * 20 + 24}px`;
        }
        // All other indented rows: compensate so value columns don't shift
        return `-${level * 20}px`;
    }

    private isDataLeaf(row: TabularReportRow): boolean {
        if (row.children?.length) { return false; }
        return !row.categoryHeader
            && !row.subTotal
            && !row.nestedTotal
            && !row.grandTotal
            && !row.collapsible;
    }

    /**
     * Flatten the hierarchical row tree into a display array, ordering each sibling group by
     * the active sort. Only includes children of rows where show !== false.
     */
    private buildDisplayRows(): void {
        this.displayRows = this.flattenRows(this.rows, 0);
    }

    private flattenRows(rows: TabularReportRow[], level: number): TabularReportRow[] {
        return this.orderRows(rows).reduce<TabularReportRow[]>((result, row) => {
            row.level = level;
            const children = (row.children?.length && row.show !== false)
                ? this.flattenRows(row.children, level + 1)
                : [];
            return [...result, row, ...children];
        }, []);
    }

    /**
     * Return a sibling list ordered by the active sort, preserving the hierarchy: report-level
     * total rows (subtotal/grand-total) are pinned to the end of their group and never sorted;
     * every other sibling (including a `nestedTotal` group header) sorts normally. The source
     * array is never reordered in place (we sort a copy of references), so the natural order is
     * intact if the sort is cleared and the row objects stay identical (collapse/indent keep working).
     */
    private orderRows(rows: TabularReportRow[]): TabularReportRow[] {
        const column = this.activeSortColumn();
        if (!column) { return rows; }

        const totals = rows.filter(r => this.isPinnedRow(r));
        const items = rows.filter(r => !this.isPinnedRow(r));
        const sorted = [...items].sort((a, b) => this.compareRows(a, b, column));
        return [...sorted, ...totals];
    }

    /** Report-level aggregate rows (subtotal/grand-total) are anchored at the end of their group
     * and excluded from the sort. `nestedTotal` is deliberately NOT pinned: it's a recurring group
     * header with its own children and must interleave with its siblings. */
    private isPinnedRow(row: TabularReportRow): boolean {
        return !!(row.grandTotal || row.subTotal);
    }

    /** The visible, sortable column matching the active sort, or null when no sort applies. */
    private activeSortColumn(): TabularReportColumn | null {
        if (!this.sortColumn || !this.sortDirection) { return null; }
        const column = this.visibleColumns.find(c => c.valueKey === this.sortColumn);
        return column?.sortable ? column : null;
    }

    /** Compare two rows by the active column, delegating to the shared type-aware comparator
     * (numeric 0/negatives correct, blanks last both directions). */
    private compareRows(a: TabularReportRow, b: TabularReportRow, column: TabularReportColumn): number {
        const type = this.sortTypeForColumn(column);
        return this._comparator.compare(
            this.effectiveSortKey(a, column, type),
            this.effectiveSortKey(b, column, type),
            this.sortDirection,
        );
    }

    /** A row's own value drives its sort position. A group header (e.g. a `nestedTotal` day-group
     * in Deposit Details) commonly carries no value of its own for the sorted column — only its
     * children do — so it falls back to the direction-appropriate extreme (max when descending,
     * min when ascending) among its descendants, so the group interleaves by the values it contains. */
    private effectiveSortKey(
        row: TabularReportRow,
        column: TabularReportColumn,
        type: 'string' | 'number' | 'date',
    ): number | string | null {
        const own = this.sortKey(row[column.valueKey], type);
        if (own !== null || !row.children?.length) { return own; }

        const childKeys = row.children
            .map(child => this.effectiveSortKey(child, column, type))
            .filter((key): key is number | string => key !== null);
        if (!childKeys.length) { return null; }

        // Pick the group's representative key via the SHARED comparator so a value-less group header
        // interleaves by the same ordering its leaves use — correct for string columns too (natural
        // "Item 2" < "Item 10"), not just numeric/date. asc → smallest child key, desc → largest.
        return childKeys.reduce((best, key) => {
            const keyIsMoreExtreme = this.sortDirection === 'desc'
                ? this._comparator.compare(key, best, 'asc') > 0
                : this._comparator.compare(key, best, 'asc') < 0;
            return keyIsMoreExtreme ? key : best;
        });
    }

    /** Resolve how a column's values compare: explicit hint wins, then numeric formats, then string.
     * The Date column carries format 'string', so consumers set sortType 'date' for chronological order. */
    private sortTypeForColumn(column: TabularReportColumn): 'string' | 'number' | 'date' {
        if (column.sortType) { return column.sortType; }
        if (this.isNumericFormat(column)) { return 'number'; }
        return 'string';
    }

    /** Normalize a cell value into a comparable key, or null when it can't be ordered (blank, or a
     * date/number that won't parse) so it sorts to the bottom. Dates become epoch ms because the
     * shared comparator does not parse ambiguous date strings. */
    private sortKey(value: unknown, type: 'string' | 'number' | 'date'): number | string | null {
        if (value == null || value === '') { return null; }
        if (type === 'date') {
            const time = this.parseSortableDate(value);
            return isNaN(time) ? null : time;
        }
        if (type === 'number') {
            const num = Number(value);
            return isNaN(num) ? null : num;
        }
        return String(value);
    }

    /** Parse the backend's US month-first date string (Carbon m/d/Y, e.g. '06/30/2026'; dashes and
     * ISO also tolerated) into epoch ms for chronological sorting. */
    private parseSortableDate(value: unknown): number {
        const text = String(value).trim();
        const parsed = moment(text, ['MM/DD/YYYY', 'M/D/YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
        return parsed.isValid() ? parsed.valueOf() : NaN;
    }

    /** Seed the active sort once (on the first non-empty columns), from the persisted preference if
     * present, else the default. Guards on column presence because the first emission is often an
     * empty array before the consumer has resolved its columns and default sort. */
    private initializeSort(): void {
        if (this._sortInitialized || !this.visibleColumns.length) { return; }
        this._sortInitialized = true;

        const stored = this.tableType ? this.prefs().sort(this.tableType)() : null;
        const initial = stored ?? this.defaultSort ?? null;
        if (initial) {
            this.sortColumn = initial.active;
            this.sortDirection = initial.direction;
        }
    }

    /** Persist the active sort (or clear it) when a `tableType` is provided. */
    private persistSort(): void {
        if (!this.tableType) { return; }
        this.prefs().setSort(
            this.tableType,
            this.sortColumn && this.sortDirection
                ? { active: this.sortColumn, direction: this.sortDirection }
                : null,
        );
    }
}
